// Historial de acceso (VLT-335 · prompt §9). access_logs existe desde 0001;
// aquí se estrena: cada evento de autenticación queda registrado vía el RPC
// vaultex.log_access() (SECURITY DEFINER, 0016) — el cliente no puede
// falsificar actor ni organización, y solo lee sus PROPIAS filas. La IP y el
// user-agent los toma la BASE de la conexión (0066, cabeceras de la petición):
// el navegador no puede declararlos por su cuenta.
import { supabase, hasBackend } from '@/lib/supabase';
import { loadFailed } from './loadError';

export type AccessAction =
  | 'login'
  | 'logout'
  | 'mfa_challenge'
  | 'password_reset_request'
  | 'password_updated'
  | 'email_verification_resent'
  | 'session_revoke_others'
  | 'login_locked' // 0069: lo escribe el portero (bloqueo por intentos fallidos)
  | 'session_revoke' // 0072: cierre remoto de una sesión
  | 'session_idle_timeout'
  | 'session_expired';

export interface AccessEvent {
  id: string;
  action: AccessAction;
  at: string; // ISO
  device: string; // etiqueta humana derivada del user-agent
  ip: string | null; // 0066: IP de la conexión (null en eventos anteriores)
}

/** Etiqueta humana de dispositivo desde el user-agent (puro, testeable).
 *  Sin librerías: solo las familias que importan para reconocer "¿ese soy yo?". */
export function describeDevice(ua: string | null | undefined): string {
  if (!ua) return '—';
  const os = /iPhone|iPad/i.test(ua) ? 'iOS'
    : /Android/i.test(ua) ? 'Android'
    : /Mac OS X|Macintosh/i.test(ua) ? 'macOS'
    : /Windows/i.test(ua) ? 'Windows'
    : /Linux/i.test(ua) ? 'Linux'
    : '';
  // Orden importa: Edge y Chrome se anuncian como Safari; Edge también como Chrome.
  const browser = /Edg\//i.test(ua) ? 'Edge'
    : /Firefox\//i.test(ua) ? 'Firefox'
    : /Chrome\//i.test(ua) ? 'Chrome'
    : /Safari\//i.test(ua) ? 'Safari'
    : '';
  if (!os && !browser) return '—';
  return [browser, os].filter(Boolean).join(' · ');
}

export interface AuthEventsService {
  /** Registra un evento de autenticación (best-effort: jamás rompe el flujo). */
  record(action: AccessAction): Promise<void>;
  /** Historial PROPIO, más reciente primero. */
  history(limit?: number): Promise<AccessEvent[]>;
}

// ─── Implementación real (RPC 0016 + lectura de filas propias) ──────────────

const realAuthEvents: AuthEventsService = {
  async record(action) {
    try {
      // Cast estrecho: el placeholder de types/database.ts no trae los
      // metadatos internos que el overload de rpc() de supabase-js 2.110
      // exige para funciones con argumentos (se irá al generar los types).
      const rpc = supabase.rpc as unknown as (
        fn: 'log_access',
        args: { p_action: string; p_user_agent?: string | null },
      ) => PromiseLike<{ error: { message: string } | null }>;
      const { error } = await rpc('log_access', {
        p_action: action,
        p_user_agent: navigator.userAgent,
      });
      if (error) console.warn('[VaulTex] authEvents.record:', error.message);
    } catch {
      // Best-effort: un fallo del log jamás bloquea el login/logout.
    }
  },

  async history(limit = 20) {
    try {
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user.id;
      if (!uid) return [];
      const { data, error } = await supabase
        .from('access_logs')
        .select('id, action, ip_address, user_agent, created_at')
        .eq('actor_id', uid)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) loadFailed('authEvents.history', error);
      if (!data) return [];
      return (data as unknown as { id: string; action: string; ip_address: string | null; user_agent: string | null; created_at: string }[])
        .map((r) => ({
          id: r.id,
          action: r.action as AccessAction,
          at: r.created_at,
          device: describeDevice(r.user_agent),
          ip: r.ip_address,
        }));
    } catch {
      return [];
    }
  },
};

// ─── Implementación demo (fixtures deterministas + sesión actual) ───────────

const DEMO_KEY = 'vaultex.demoAccessLog';

function demoSeed(): AccessEvent[] {
  // Historial creíble y ESTABLE para demo/E2E/visual (fechas fijas).
  return [
    { id: 'ae-3', action: 'login', at: '2026-08-31T14:05:00Z', device: 'Chrome · macOS', ip: '203.0.113.24' },
    { id: 'ae-2', action: 'mfa_challenge', at: '2026-08-31T14:05:00Z', device: 'Chrome · macOS', ip: '203.0.113.24' },
    { id: 'ae-1', action: 'login', at: '2026-08-28T09:12:00Z', device: 'Safari · iOS', ip: '198.51.100.7' },
  ];
}

function demoRead(): AccessEvent[] {
  try {
    const raw = localStorage.getItem(DEMO_KEY);
    if (raw) return (JSON.parse(raw) as AccessEvent[]).map((e) => ({ ...e, ip: e.ip ?? null }));
  } catch {
    // storage bloqueado → seed
  }
  return demoSeed();
}

const fixtureAuthEvents: AuthEventsService = {
  async record(action) {
    const list = demoRead();
    list.unshift({
      id: `ae-${Date.now()}`,
      action,
      at: new Date().toISOString(),
      device: describeDevice(navigator.userAgent),
      ip: null, // la demo no tiene servidor que vea la conexión
    });
    try {
      localStorage.setItem(DEMO_KEY, JSON.stringify(list.slice(0, 30)));
    } catch {
      // sin storage no hay persistencia demo; no pasa nada
    }
  },

  async history(limit = 20) {
    return demoRead().slice(0, limit);
  },
};

export const authEventsService: AuthEventsService = hasBackend ? realAuthEvents : fixtureAuthEvents;
